import React, { useEffect, Fragment } from 'react';

import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { GlobalErrorHandler } from 'app/src/components/GlobalErrorHandler';

import { domReady, initializeLayers } from './doc-init';

import Perf, { PerfKeys } from 'app/common/debug/perf';
import { flushPerformanceMetrics } from 'app/common/debug/flushPerformanceMetrics';

import { Banners } from 'app/common/components/Banners/Banners';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { LayerManager } from 'app/src/components/LayerManager';
import { ReactionAlerts } from 'app/src/components/ReactionAlerts';
import { MemberProfileAlerts } from 'app/src/components/MemberProfileAlerts';
import Header from 'app/gamma/src/components/header';
import LogoutTrigger from 'app/gamma/src/components/logout-trigger';
import Overlays from 'app/gamma/src/components/overlays';
import BoardsMenu from 'app/gamma/src/components/boards-menu';
import { MinimumVersionUpdater } from 'app/src/components/MinimumVersionUpdater';

import Backbone from 'app/scripts/lib/backbone';
import Controller from 'app/scripts/controller';
import 'app/scripts/debug/client-version-header';
import Dates from 'app/scripts/lib/dates';
import Monitor from 'app/scripts/lib/monitor';
import WindowSize from 'app/scripts/lib/window-size';
import SessionStatus from 'app/scripts/network/session-status';
import ModelCache from 'app/scripts/db/model-cache';
import { classicUpdaterClient } from 'app/scripts/network/classic-updater-client';
import { ColorBlindSupport } from 'app/src/components/ColorBlindSupport';

import './Chrome.less';
import { PopoverBoundary } from '@trello/nachos';

export const Chrome = () => {
  useEffect(() => {
    domReady();

    initializeLayers();

    const classicBody = $('#trello-root');

    setInterval(() => {
      Dates.update(classicBody);
      Dates.trigger('renderInterval');
    }, 10 * 1000);

    SessionStatus.start();
    Monitor.init();
    Controller.start().then(() => {
      Backbone.history.start({ pushState: true });
    });
    WindowSize.ensureRun();

    classicUpdaterClient.subscribe(({ typeName, delta }) => {
      ModelCache.enqueueRpcDelta(typeName, delta);
    });
    Perf.stopMeasurement(PerfKeys.FIRST_RENDER);
    Perf.reportStartupMetrics();

    // sending this to the next frame to ensure app and html loaded have
    // a chance to stop measurement before we send these
    setTimeout(() => {
      flushPerformanceMetrics({
        buffered: Perf.getCachedMetrics(),
        flush: () => Perf.clearCachedMetrics(),
      });
    }, 0);
  }, []);

  return (
    <ErrorBoundary
      errorHandlerComponent={GlobalErrorHandler}
      sendCrashEvent={true}
      tags={{ fromGlobalErrorBoundary: true }}
    >
      <MinimumVersionUpdater>
        <ColorBlindSupport />
        <LayerManager>
          <ComponentWrapper>
            <BoardsMenu />
          </ComponentWrapper>

          <p id="notification" />
          <div id="surface">
            <ComponentWrapper>
              <Header />
            </ComponentWrapper>
            <PopoverBoundary>
              <ComponentWrapper>
                <Banners />
              </ComponentWrapper>
              <div id="banners" />
              <div id="content" />
            </PopoverBoundary>
            <div id="footer">
              <div id="contact-us" style={{ display: 'none' }}>
                If you're having trouble loading Trello,{' '}
                <a
                  href="http://help.trello.com/article/736-troubleshooting-browser-issues-with-trello"
                  target="support"
                >
                  check out our troubleshooting guide
                </a>{' '}
                or{' '}
                <a
                  href="/contact?context=load-failure&source=from%20in%20app%20footer"
                  target="contact"
                >
                  contact us
                </a>
                !
              </div>
              <div id="footer-chrome" style={{ display: 'none' }} />
            </div>
          </div>

          <ComponentWrapper>
            <Fragment>
              <ReactionAlerts />
              <LogoutTrigger />
            </Fragment>
          </ComponentWrapper>

          <ComponentWrapper>
            <Fragment>
              <MemberProfileAlerts />
              <Overlays />
            </Fragment>
          </ComponentWrapper>
        </LayerManager>
        <div className="window-overlay">
          <div className="window">
            <a className="focus-dummy" href="#" />
            <div className="window-wrapper" />
          </div>
        </div>
        <div className="pop-over" />
        <div className="tooltip-container" />
        <div id="clipboard-container" />
      </MinimumVersionUpdater>
    </ErrorBoundary>
  );
};
